import { useState } from "react";
import { GraduationCap, Award, Instagram, Sparkles, ChevronDown, ArrowRight } from "lucide-react";
import { Heart, Star, Squiggle } from "./Illustrations";

const teamMembers = [
  { name: "Κουρσάρη Άννα", role: "Λογοθεραπεύτρια", img: "/images/team/anna.jpg", circle: "border-brand-pink" },
  { name: "Ναταλία Λαρίση", role: "Εργοθεραπεύτρια", img: "/images/team/natalia.jpg", circle: "border-brand-yellow" },
  { name: "Σοφία Γιαννακοπούλου", role: "Ψυχολόγος", img: "/images/team/sofia.jpg", circle: "border-brand-blue" },
  { name: "Αθηνά Αλεξοπούλου", role: "Ψυχολόγος", img: "/images/team/athina.jpg", circle: "border-brand-purple" },
];

export default function Team() {
  const [showMore, setShowMore] = useState(false);

  return (
    <section
      id="omada"
      data-testid="team-section"
      className="py-24 md:py-32 bg-brand-bg relative overflow-hidden"
    >
      <Star className="absolute top-20 left-1/4 animate-pulse-soft" size={26} color="#FBE4A8" />
      <Heart className="absolute top-32 right-10 animate-pulse-soft hidden md:block" size={32} color="#FBC4D0" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white border-2 border-brand-yellow mb-6 -rotate-2">
            <Star size={18} color="#FBE4A8" />
            <span className="font-body text-sm font-bold text-brand-text">Η Ομάδα μας</span>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-text leading-tight">
            Με{" "}
            <span className="font-hand text-5xl sm:text-6xl lg:text-7xl text-brand-primary">
              αγάπη
            </span>
            <br />
            για κάθε παιδί
            <Heart size={36} className="inline-block ml-2 animate-pulse-soft align-middle" color="#F4A899" />
          </h2>
          <Squiggle className="mx-auto my-5" width={170} color="#FBE4A8" />
          <p className="font-body text-base sm:text-lg text-brand-textSoft leading-relaxed">
            Στο κέντρο μας εξειδικευμένοι θεραπευτές όλων των ειδικοτήτων
            εργάζονται με αγάπη και συνεχή επαγγελματική κατάρτιση, με στόχο τη
            στήριξη κάθε παιδιού και της οικογένειάς του.
          </p>
        </div>

        {/* Featured: Υπεύθυνη Κέντρου */}
        <div
          data-testid="director-featured-card"
          className="relative bg-brand-pinkSoft rounded-[2.5rem] border-4 border-dashed border-brand-pink p-7 md:p-10 -rotate-1 shadow-[0_12px_0_-3px_rgba(91,79,70,0.15)]"
        >
          <Star className="absolute -top-5 -right-3 z-10 animate-wiggle" size={40} color="#FBE4A8" />
          <div className="flex flex-col sm:flex-row items-center gap-6 md:gap-10">
            <div className="relative flex-shrink-0">
              <div className="w-36 h-36 md:w-44 md:h-44 rounded-full overflow-hidden border-4 border-dashed border-brand-primary/50 bg-white rotate-2">
                <img src="/images/team/anna.jpg" alt="Κουρσάρη Άννα — ζωγραφιστό πορτρέτο" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <Heart size={26} className="absolute -top-1 -right-1 animate-pulse-soft" color="#F4A899" />
            </div>
            <div className="text-center sm:text-left">
              <div className="inline-block px-3 py-1 rounded-full bg-brand-primary text-white font-bold text-xs mb-3 -rotate-2 shadow-[0_2px_0_#5B4F46]">
                Υπεύθυνη Κέντρου
              </div>
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-brand-text">
                Κουρσάρη{" "}
                <span className="font-hand text-3xl md:text-4xl text-brand-primary">Άννα</span>
              </h3>
              <p className="font-heading font-bold text-brand-primaryHover mt-1">
                Λογοθεραπεύτρια — MSc Ειδικής Αγωγής
              </p>
              <p className="font-body text-brand-textSoft leading-relaxed mt-3">
                Υπεύθυνη για τη δημιουργία των εξατομικευμένων προγραμμάτων,
                τον συντονισμό της ομάδας και την εποπτεία όλων των
                παρεχόμενων υπηρεσιών.
              </p>
              <button
                data-testid="director-more-button"
                onClick={() => setShowMore(!showMore)}
                className="inline-flex items-center gap-2 mt-5 px-6 py-2.5 rounded-full bg-white border-2 border-dashed border-brand-primary text-brand-primaryHover font-heading font-bold text-sm hover:bg-brand-yellowSoft hover:scale-105 transition-all duration-300"
              >
                {showMore ? "Λιγότερα" : "Περισσότερα για την υπεύθυνη"}
                <ChevronDown size={16} className={`transition-transform duration-300 ${showMore ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>

          {showMore && (
            <div data-testid="director-full-bio" className="mt-8 pt-8 border-t-2 border-dashed border-brand-pink/60">
              <p className="font-body text-base sm:text-lg text-brand-textSoft leading-relaxed">
                Με πολυετή κλινική εμπειρία στον χώρο των ειδικών θεραπειών,
                στηρίζω κάθε παιδί και κάθε οικογένεια με αφοσίωση, επιστημονική
                τεκμηρίωση και αληθινό ενδιαφέρον — σε ένα περιβάλλον γεμάτο
                εμπιστοσύνη και ζεστασιά.
              </p>
              <p className="mt-4 font-body text-base text-brand-textSoft leading-relaxed">
                Παρακολουθώ συνεχώς σεμινάρια και εκπαιδεύσεις στις πιο σύγχρονες
                θεραπευτικές μεθόδους, ώστε κάθε παρέμβαση να βασίζεται στις πιο
                αποτελεσματικές και διεθνώς αναγνωρισμένες πρακτικές.
              </p>
              <div className="mt-7 grid md:grid-cols-1 gap-4">
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/70 border-2 border-dashed border-brand-text/15 -rotate-1 hover:rotate-0 transition-transform" data-testid="director-edu-1">
                  <div className="w-12 h-12 rounded-xl bg-brand-pink flex items-center justify-center flex-shrink-0 border-2 border-brand-text/20">
                    <GraduationCap className="text-brand-text" size={22} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-brand-text">Πτυχίο Λογοθεραπείας</div>
                    <div className="font-body text-sm text-brand-textSoft mt-1">
                      Canterbury Christ Church University (CCCU), Αγγλία
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/70 border-2 border-dashed border-brand-text/15 rotate-1 hover:rotate-0 transition-transform" data-testid="director-edu-2">
                  <div className="w-12 h-12 rounded-xl bg-brand-yellow flex items-center justify-center flex-shrink-0 border-2 border-brand-text/20">
                    <Award className="text-brand-text" size={22} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-brand-text">Μεταπτυχιακό στην Ειδική Αγωγή</div>
                    <div className="font-body text-sm text-brand-textSoft mt-1">
                      Εξειδίκευση στις αναπτυξιακές διαταραχές και μαθησιακές δυσκολίες
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/70 border-2 border-dashed border-brand-text/15 -rotate-1 hover:rotate-0 transition-transform" data-testid="director-edu-3">
                  <div className="w-12 h-12 rounded-xl bg-brand-blue flex items-center justify-center flex-shrink-0 border-2 border-brand-text/20">
                    <Sparkles className="text-brand-text" size={22} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-brand-text">Τομείς Εξειδίκευσης</div>
                    <div className="font-body text-sm text-brand-textSoft mt-1">
                      Διαταραχές Αυτιστικού Φάσματος (ΔΑΦ) · Πρώιμη Παρέμβαση · Καθυστέρηση Λόγου · Διαταραχές Επικοινωνίας
                    </div>
                  </div>
                </div>
              </div>
              <a
                href="https://instagram.com/orizontes.anaptixis"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-white border-2 border-brand-pink text-brand-primary font-bold text-sm hover:bg-brand-pinkSoft transition-colors shadow-[0_3px_0_#5B4F46]/15"
                data-testid="director-instagram"
              >
                <Instagram size={18} />
                @orizontes.anaptixis
              </a>
            </div>
          )}
        </div>

        {/* Team members grid */}
        <div className="mt-20" data-testid="team-members-grid">
          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-brand-text text-center">
            Η διεπιστημονική μας{" "}
            <span className="font-hand text-3xl sm:text-4xl text-brand-primary">ομάδα</span>
          </h3>
          <Squiggle className="mx-auto mt-3 mb-10" width={150} color="#FBC4D0" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {teamMembers.map((m, i) => (
              <div
                key={m.name}
                data-testid={`team-member-${i}`}
                className={`bg-white rounded-[2rem] p-6 md:p-8 border-3 border-dashed border-brand-text/15 text-center ${i % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0 hover:scale-[1.03] transition-transform duration-500 shadow-[0_8px_0_-2px_rgba(91,79,70,0.12)]`}
              >
                <div className={`mx-auto w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-3 border-dashed ${m.circle} bg-white rotate-2 mb-4`}>
                  <img src={m.img} alt={`${m.name} — ζωγραφιστό πορτρέτο`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="font-heading font-bold text-base md:text-lg text-brand-text leading-tight">
                  {m.name}
                </div>
                <div className="font-hand text-lg text-brand-primary mt-1">{m.role}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 max-w-2xl mx-auto bg-brand-blueSoft border-3 border-dashed border-brand-blue rounded-[2rem] p-6 text-center rotate-1">
            <p className="font-body text-brand-textSoft">
              <Heart size={20} className="inline-block mr-2 align-middle animate-pulse-soft" color="#F4A899" />
              Συνεργαζόμαστε, εξελισσόμαστε και στηρίζουμε κάθε παιδί με σεβασμό
              στη μοναδικότητά του.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
